import React from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '/images/facilities/Junior-Classroom.jpg') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const STAGES = [
  {
    step: '01',
    title: 'Pre-Primary Foundational Years',
    classes: 'Pre-Nursery, Nursery, KG-I & KG-II',
    age: 'Ages 3 to 5 Years',
    icon: 'fa-shapes',
    desc: 'Focus on joyful sensory exploration, language acquisition, phonetics, rhyme, fine motor dexterity, and social adaptation through guided play.',
    keyPoints: [
      'Thematic curiosity corners & sensory discovery',
      'Phonetic awareness & picture storytelling',
      'Number sense, sorting & geometric patterns',
      'Gross motor agility, music, rhythm & dance'
    ]
  },
  {
    step: '02',
    title: 'Lower Primary Exploration',
    classes: 'Grades I & II',
    age: 'Ages 6 to 7 Years',
    icon: 'fa-book-open-reader',
    desc: 'Structured transition into formal literacy, expressive bilingual vocabulary, neat handwriting, fundamental arithmetic, and environmental awareness.',
    keyPoints: [
      'Independent reading & listening comprehension',
      'Concrete-to-pictorial arithmetic & problem sums',
      'Environmental studies & practical nature observations',
      'Creative arts, pottery, theatre & swimming fundamentals'
    ]
  },
  {
    step: '03',
    title: 'Upper Primary Mastery',
    classes: 'Grades III, IV & V',
    age: 'Ages 8 to 10 Years',
    icon: 'fa-microscope',
    desc: 'Deepening conceptual clarity, scientific temper, analytical mathematics, introductory coding & digital literacy, and collaborative peer projects.',
    keyPoints: [
      'Inquiry-driven science experiments & lab visits',
      'Analytical mathematics & real-world word problems',
      'Introductory computational coding & digital literacy',
      'Inter-house sports, athletics & performing arts studios'
    ]
  }
];

const JuniorSchoolProgrammePage = () => {
  const pageData = usePage('junior-school-programme');
  const sections = pageData.sections || {};
  const banner = sections.banner || pageData.banner || {};

  const title = banner.title || pageData.title || 'Junior School Programme';
  const subtitle = banner.subtitle || 'Foundational Literacy, Sensory Discovery & Joyful Learning (Pre-Nursery to Grade V)';
  const breadcrumb = banner.breadcrumb || 'JUNIOR SCHOOL';
  const bannerImgSrc = resolveImage(pageData.bannerImage || pageData.banner_image, '/images/facilities/Junior-Classroom.jpg');

  const overview = sections.overview || {};
  const overviewKicker = overview.kicker || 'FOUNDATIONAL & PRIMARY YEARS • PRE-NURSERY TO GRADE V';
  const overviewHeading = overview.heading || 'Where Natural Wonder Ignites a Lifelong Love for Learning';
  const overviewParas = overview.paragraphs
    ? (Array.isArray(overview.paragraphs) ? overview.paragraphs : overview.paragraphs.split('\n\n').filter(Boolean))
    : [
        'The early and primary years represent the most formative window of a child’s intellectual, emotional, and physical development. At St. Andrews World School, Sonipat, our Junior School Programme is thoughtfully crafted to transform curiosity into capability.',
        'We embrace an activity-centric, joyful pedagogy aligned with the CBSE Curriculum and NEP 2020 Foundational Framework. Rather than burdening young minds with rote memorization, our teachers nurture the innate desire to ask “Why?” and empower students to discover answers through tangible experience.',
        'From temperature-controlled, ergonomic smart classrooms to safe outdoor discovery play areas, our junior wing offers a cheerful, low-stress environment where every child feels cherished, heard, and inspired every single day.'
      ];
  const quoteText = overview.quote_text || '“Every child arrives with an eager imagination. Our mission is to protect that spark, build rock-solid foundational literacy and numeracy, and cultivate confident, kind-hearted individuals.”';
  const quoteAuthor = overview.quote_author || 'Primary Academic Directorate • SAWS Sonipat';
  const photoBadge = overview.photo_badge || 'Child-Centric Campus';
  const photoTitle = overview.photo_title || 'Vibrant, Interactive Early Learning Spaces';
  const showcaseImg = resolveImage(overview.image, bannerImgSrc);

  const stagesKicker = sections.stages_kicker || 'LEARNING ROADMAP';
  const stagesHeading = sections.stages_heading || 'Age-Calibrated Learning Across Primary Stages';
  const stagesSubtitle = sections.stages_subtitle || 'Our curriculum is carefully graduated to support each stage of physical, emotional, and intellectual readiness.';
  const stagesList = (sections.stages && sections.stages.length ? sections.stages : STAGES).map(st => ({
    ...st,
    keyPoints: Array.isArray(st.keyPoints)
      ? st.keyPoints
      : (typeof st.keyPoints === 'string' ? st.keyPoints.split('\n').map(p => p.trim()).filter(Boolean) : (st.keyPoints || []))
  }));

  return (
    <>
      <SEO slug="junior-school-programme" title="Junior School Programme | St. Andrews World School, Sonipat"
        description="Explore the Junior School Programme (Pre-Nursery to Grade V) at St. Andrews World School, Sonipat. Child-centric learning, phonics, experiential STEM, and safe nurturing campus."
        keywords="Junior School Programme, Pre-Primary School Sonipat, Nursery Admission, Primary School Sonipat, CBSE Junior School, SAWS Sonipat"
      />

      {/* 1. HERO BANNER */}
      <PageBanner
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        bgImage={bannerImgSrc}
      />

      {/* 2. OPEN OVERVIEW & PHILOSOPHY (No Box) */}
      <section className="bg-white py-16 md:py-24 border-b border-gray-100">
        <div className="container max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Narrative */}
            <div className="lg:col-span-7">
              <Reveal>
                <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-2 block">
                  {overviewKicker}
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-primary leading-tight mb-5">
                  {overviewHeading}
                </h2>
                <div className="w-16 h-1 bg-secondary rounded-full mb-6" />

                <div className="space-y-4 text-body text-[16px] md:text-[17px] leading-[1.8]">
                  {overviewParas.map((p, pIdx) => (
                    <p key={pIdx}>{p}</p>
                  ))}
                </div>

                {/* Quote Callout */}
                {quoteText && (
                  <div className="mt-8 p-5 md:p-6 rounded-2xl bg-[#f9f8f4] border-l-4 border-secondary">
                    <p className="font-serif italic text-primary text-base md:text-lg font-medium leading-relaxed">
                      {quoteText}
                    </p>
                    {quoteAuthor && (
                      <span className="text-xs font-bold text-secondary uppercase tracking-widest block mt-2">
                        {quoteAuthor}
                      </span>
                    )}
                  </div>
                )}
              </Reveal>
            </div>

            {/* Right Photo Frame (Open, No Card Box) */}
            <div className="lg:col-span-5">
              <Reveal delayMs={120}>
                <div className="relative group">
                  <div className="absolute -inset-3.5 rounded-3xl bg-secondary/15 -z-10 transform -rotate-1 group-hover:rotate-0 transition-transform duration-500" />
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 shadow-xl">
                    <img
                      src={showcaseImg}
                      alt="Junior Classroom at St Andrews"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent pointer-events-none" />
                    
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      {photoBadge && (
                        <span className="inline-block px-3 py-1 rounded-full bg-secondary text-primary font-bold text-xs uppercase tracking-wider mb-1">
                          {photoBadge}
                        </span>
                      )}
                      {photoTitle && (
                        <h4 className="text-lg md:text-xl font-display font-bold text-white">
                          {photoTitle}
                        </h4>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </section>

      {/* 3. DEVELOPMENTAL STAGES (Open Alternating Flow, No Box) */}
      <section className="bg-[#f9f8f4] py-16 md:py-24">
        <div className="container max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-2 block">
                {stagesKicker}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-primary mb-4">
                {stagesHeading}
              </h2>
              <div className="w-16 h-1 bg-secondary mx-auto mb-4 rounded-full" />
              {stagesSubtitle && (
                <p className="text-body text-[16px] md:text-[17px] leading-relaxed">
                  {stagesSubtitle}
                </p>
              )}
            </div>
          </Reveal>

          <div className="space-y-16 lg:space-y-20">
            {stagesList.map((st, idx) => {
              const isReversed = idx % 2 === 1;
              return (
                <div key={idx} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                  
                  {/* Visual Stage Indicator */}
                  <div className={`lg:col-span-5 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <Reveal delayMs={isReversed ? 100 : 0}>
                      <div className="p-8 md:p-10 rounded-3xl bg-white border border-gray-200/80 shadow-sm relative overflow-hidden">
                        <div className="text-secondary font-display font-bold text-6xl md:text-7xl opacity-30 absolute top-4 right-6">
                          {st.step}
                        </div>
                        <div className="w-14 h-14 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center text-2xl mb-6 shadow-xs">
                          <i className={`fas ${st.icon || 'fa-shapes'}`} />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-wider text-secondary block mb-1">
                          Stage {st.step}
                        </span>
                        <h3 className="text-2xl md:text-3xl font-display font-bold text-primary mb-2">
                          {st.title}
                        </h3>
                        {st.classes && (
                          <div className="inline-block px-3 py-1 rounded-full bg-primary/5 text-primary text-xs font-semibold mb-3 border border-primary/10">
                            {st.classes}
                          </div>
                        )}
                        {st.age && (
                          <div className="text-xs text-muted font-medium flex items-center gap-1.5">
                            <i className="far fa-clock text-secondary" />
                            <span>{st.age}</span>
                          </div>
                        )}
                      </div>
                    </Reveal>
                  </div>

                  {/* Narrative & Key Points */}
                  <div className={`lg:col-span-7 ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    <Reveal delayMs={isReversed ? 0 : 100}>
                      <div className="space-y-4">
                        <p className="text-body text-[16px] md:text-[17px] leading-[1.8]">
                          {st.desc}
                        </p>
                        
                        {st.keyPoints && st.keyPoints.length > 0 && (
                          <div className="pt-2">
                            <h4 className="text-sm font-bold uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                              <i className="fas fa-bullseye text-secondary text-xs" />
                              <span>Core Developmental Milestones:</span>
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {st.keyPoints.map((pt, pIdx) => (
                                <div
                                  key={pIdx}
                                  className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-gray-200/70 text-[14px] md:text-[15px] text-body"
                                >
                                  <i className="fas fa-circle-check text-secondary text-sm mt-0.5 shrink-0" />
                                  <span>{pt}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Reveal>
                  </div>

                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default JuniorSchoolProgrammePage;
